/**
 * Standalone Viseme Generator Service
 *
 * This service generates Polly-style visemes from arbitrary text without
 * requiring phoneme data or external dependencies.
 *
 * Uses established phoneme-to-viseme mapping standards:
 * - ARPAbet phoneme set
 * - CMU Pronouncing Dictionary patterns
 * - Microsoft SAPI viseme categories
 */



// Exact Polly viseme mapping based on analysis of all samples
const PHONEME_TO_VISEME_MAP = {
  // Vowels (exact Polly mapping)
  'AA': 'a', 'AO': 'a', 'AH': 'a', 'A': 'a', // Open back vowels
  'AE': 'a', 'AW': 'a', // Open front vowels
  'EH': 'e', 'EY': 'e', 'E': 'E', // Mid front vowels (Polly uses both 'e' and 'E')
  'ER': 'e', // R-colored vowels (Polly uses 'e' for some)
  'IH': 'i', 'IY': 'i', 'I': 'i', // High front vowels
  'OW': 'o', 'OY': 'o', 'O': 'O', // Mid back vowels (Polly uses 'O' for some cases)
  'UH': 'u', 'UW': 'u', 'U': 'u', // High back vowels

  // Consonants (exact Polly mapping)
  'B': 'p', 'P': 'p', 'M': 'p', // Bilabial stops/nasals
  'F': 'f', 'V': 'f', // Labiodental fricatives
  'W': 'u', // Labial-velar approximant (Polly uses 'u')
  'D': 't', 'T': 't', 'N': 't', 'L': 't', // Alveolar stops/nasals/liquids
  'S': 's', 'Z': 's', // Alveolar fricatives
  'R': 'r', // Alveolar approximant
  'G': 'k', 'K': 'k', 'NG': 'k', // Velar stops/nasals
  'H': 'k', // Glottal fricative (Polly uses 'k')
  'Y': 'i', // Palatal approximant
  'CH': 't', 'JH': 't', // Affricates (Polly uses 't')
  'SH': 'S', 'ZH': 'S', // Fricatives (Polly uses 'S')
  'TH': 'T', 'DH': 'T', // Interdentals (Polly uses 'T')
  'KW': 'k', // Special case for 'qu'
  '@': '@', // Schwa sound (Polly uses '@' frequently)
  'sil': 'sil', // Silence

  // Default mappings for unknown phonemes
  'default': 'a'
};

// Letter combinations that should be treated as single phonemes
const LETTER_COMBINATIONS = {
  'th': 'TH', 'ch': 'CH', 'sh': 'SH', 'ph': 'F', 'wh': 'W',
  'ck': 'K', 'ng': 'N', 'qu': 'KW', 'kn': 'N', 'wr': 'R',
  'mb': 'M', 'gn': 'N'
};

// Letter-to-phoneme approximation (for when we don't have phoneme data)
const LETTER_TO_PHONEME_MAP = {
  // Vowels (basic mappings, schwa detection happens in detectSchwaSounds)
  'a': 'AA', 'A': 'AA',
  'e': 'EH', 'E': 'EH',
  'i': 'IH', 'I': 'IH',
  'o': 'OW', 'O': 'OW',
  'u': 'UH', 'U': 'UH',
  'y': 'IY', 'Y': 'IY', // Y often acts as a vowel

  // Consonants
  'b': 'B', 'B': 'B', 'p': 'P', 'P': 'P', 'm': 'M', 'M': 'M',
  'f': 'F', 'F': 'F', 'v': 'V', 'V': 'V', 'w': 'W', 'W': 'W',
  'd': 'D', 'D': 'D', 't': 'T', 'T': 'T', 'n': 'N', 'N': 'N', 'l': 'L', 'L': 'L',
  's': 'S', 'S': 'S', 'z': 'Z', 'Z': 'Z', 'r': 'R', 'R': 'R',
  'g': 'G', 'G': 'G', 'k': 'K', 'K': 'K', 'c': 'K', 'C': 'K',
  'h': 'H', 'H': 'H', 'j': 'Y', 'J': 'Y',

    // Numbers (phoneme approximations - more granular)
  '0': 'Z', '1': 'W', '2': 'T', '3': 'TH', '4': 'F',
  '5': 'F', '6': 'S', '7': 'S', '8': 'E', '9': 'N',

  // Default for unknown characters
  'default': 'AA'
};

// Timing constants based on phoneme characteristics
const PHONEME_DURATION = {
  // Vowels (shorter duration for more animation)
  VOWEL_SHORT: 100,    // Unstressed vowels (more animated)
  VOWEL_LONG: 160,     // Stressed vowels (more animated)

  // Consonants (shorter duration for more animation)
  CONSONANT_SHORT: 60,   // Quick consonants (more animated)
  CONSONANT_MEDIUM: 90,  // Standard consonants (more animated)
  CONSONANT_LONG: 120,   // Final consonants (more animated)

  // Word boundaries (shorter for more continuous animation during speech)
  WORD_SPACING: 80,      // Space between words (more animated)
  PHRASE_SPACING: 150    // Space between phrases (slightly reduced)
};

/**
 * Generates random visemes for a specified duration starting at a given time
 * @param {number} startTime - Time in milliseconds to start the visemes at
 * @param {number} duration - Duration in milliseconds to generate visemes for
 * @returns {Array} Array of viseme objects with correct timing
 */
function generateRandomVisemes(startTime = 0, duration = 60000) {
  const visemesChars = ['u', 't', 's', 'o', 'i', 'r', 'e', 'a'];
  const silentViseme = 'sil';
  const randomVisemes = [];

  let currentTime = startTime;
  let inSilent = false;
  const endTime = startTime + duration;

  while (currentTime < endTime) {
    const randomNum = Math.random();
    if (!inSilent && randomNum >= 0.85) { // Reduced from 0.9 to 0.85 for more natural movement
      inSilent = true;
    } else {
      inSilent = false;
    }

    randomVisemes.push({
      type: 'viseme',
      value: inSilent ? silentViseme : visemesChars[Math.floor(Math.random() * visemesChars.length)],
      time: currentTime
    });

    currentTime += inSilent ? 300 : 80; // Faster visemes during speech, same silent duration
  }

  return randomVisemes;
}

/**
 * Creates random speech marks for fallback scenarios
 * @returns {Object} Polly-style speech marks object with random visemes
 */
function createRandomSpeechMarks() {
  const randomVisemes = generateRandomVisemes(0, 60000); // 60 seconds starting at time 0

  return [
    {
      words: [
        {
          type: 'word',
          visemes: randomVisemes
        }
      ]
    }
  ];
}

/**
 * Parses duration from pause tag attributes
 * @param {string} tag - The pause tag
 * @returns {number} Duration in milliseconds
 */
function parsePauseDuration(tag) {
  // Common duration attributes
  const durationPatterns = [
    /time\s*=\s*["']([^"']+)["']/i,      // time="500ms"
    /duration\s*=\s*["']([^"']+)["']/i,   // duration="500ms"
    /strength\s*=\s*["']([^"']+)["']/i,   // strength="medium"
    /length\s*=\s*["']([^"']+)["']/i      // length="500ms"
  ];

  for (const pattern of durationPatterns) {
    const match = tag.match(pattern);
    if (match) {
      const value = match[1].toLowerCase();

      // Parse numeric values with units
      const numericMatch = value.match(/^(\d+(?:\.\d+)?)\s*(ms|s|m)?$/);
      if (numericMatch) {
        const num = parseFloat(numericMatch[1]);
        const unit = numericMatch[2] || 'ms';

        switch (unit) {
          case 's': return num * 1000;  // seconds to ms
          case 'm': return num * 60000; // minutes to ms
          default: return num;           // already in ms
        }
      }

      // Parse strength values
      switch (value) {
        case 'x-weak':
        case 'xweak': return 100;
        case 'weak': return 200;
        case 'medium':
        case 'medium-weak': return 300;
        case 'medium-strong': return 400;
        case 'strong': return 500;
        case 'x-strong':
        case 'xstrong': return 600;
        default: return 200; // default fallback
      }
    }
  }

  return 200; // default fallback
}

/**
 * Processes pause tags and converts them to silent visemes
 * @param {string} text - The text with pause tags
 * @returns {Object} Object with cleaned text and pause positions
 */
function processPauseTags(text) {
  const pausePositions = [];
  let cleanedText = text;

  // Common pause tag patterns
  const pausePatterns = [
    /<break[^>]*\/?>/gi,           // <break> or <break/>
    /<pause[^>]*\/?>/gi,           // <pause> or <pause/>
    /<silence[^>]*\/?>/gi,         // <silence> or <silence/>
    /<silent[^>]*\/?>/gi,          // <silent> or <silent/>
    /<rest[^>]*\/?>/gi,            // <rest> or <rest/>
    /<wait[^>]*\/?>/gi             // <wait> or <wait/>
  ];

  // Find and mark pause positions
  pausePatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const duration = parsePauseDuration(match[0]);
      pausePositions.push({
        start: match.index,
        end: match.index + match[0].length,
        tag: match[0],
        duration: duration
      });
    }
  });

  // Remove pause tags from text
  pausePatterns.forEach(pattern => {
    cleanedText = cleanedText.replace(pattern, '');
  });

  return {
    text: cleanedText,
    pausePositions: pausePositions.sort((a, b) => a.start - b.start)
  };
}

/**
 * Strips HTML/XML tags from text
 * @param {string} text - The text with tags
 * @returns {string} The text without tags
 */
function stripTags(text) {
  return text.replace(/<[^>]*>/g, '');
}

/**
 * Gets Polly's exact viseme sequence for a number
 * @param {string} number - The number (0-9)
 * @returns {Array} Array of viseme objects
 */
function getPollyNumberVisemes(number) {
  // Polly's exact viseme sequences for each number
  const pollyNumberSequences = {
    '0': ['s', 'i', 'r', 'o'],
    '1': ['u', 'E', 't'],
    '2': ['t', 'u'],
    '3': ['T', 'r', 'i'],
    '4': ['f', 'O', 'r'],
    '5': ['f', 'a', 'f'],
    '6': ['s', 'i', 'k', 's'],
    '7': ['s', 'E', 'f', 't'],
    '8': ['e', 't'],
    '9': ['t', 'a', 't']
  };

  const sequence = pollyNumberSequences[number] || ['t'];
  const visemes = [];
  let currentTime = 0;

  for (const viseme of sequence) {
    const duration = 60 + Math.random() * 30; // 60-90ms with variation (faster for more animation)
    visemes.push({
      value: viseme,
      time: currentTime,
      duration: duration
    });
    currentTime += duration;
  }

  return visemes;
}

/**
 * Detects schwa sounds in words based on stress patterns
 * @param {string} word - The word to analyze
 * @returns {Array} Array of phoneme objects with schwa detection
 */
function detectSchwaSounds(word) {
  const lowerWord = word.toLowerCase();
  const phonemes = [];
  let i = 0;

  // Common function words that often have schwa
  const functionWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'among'];

  // If it's a function word, high chance of schwa
  const isFunctionWord = functionWords.includes(lowerWord);

  while (i < lowerWord.length) {
    let found = false;

    // Check for 2-letter combinations first
    for (const combo of Object.keys(LETTER_COMBINATIONS)) {
      if (lowerWord.substr(i, 2) === combo) {
        phonemes.push({
          text: combo,
          phoneme: LETTER_COMBINATIONS[combo],
          start: i,
          end: i + 2
        });
        i += 2;
        found = true;
        break;
      }
    }

    if (!found) {
      // Single letter
      const letter = lowerWord[i];

      // Check for schwa conditions
      let phoneme = LETTER_TO_PHONEME_MAP[letter];

              // Convert to schwa in certain conditions
        if (isVowel(letter)) {
          // Schwa rules:
          // 1. In function words (the, a, an, etc.)
          // 2. In unstressed syllables (not first syllable of multi-syllable words)
          // 3. In certain common patterns

          const isFirstSyllable = i === 0 || !lowerWord.slice(0, i).match(/[aeiou]/);
          const isMultiSyllable = (lowerWord.match(/[aeiou]/g) || []).length > 1;

          if (isFunctionWord || (!isFirstSyllable && isMultiSyllable)) {
            phoneme = '@'; // Schwa
          }

          // Special case: 'o' in certain contexts can be 'O' viseme
          if (letter === 'o' && !isFunctionWord && isFirstSyllable) {
            // Check for words that commonly use 'O' viseme
            const oWords = ['out', 'loud', 'now', 'how', 'cow', 'down', 'town', 'found', 'round'];
            if (oWords.includes(lowerWord)) {
              phoneme = 'O'; // Use 'O' viseme for these words
            }
          }

          // Special case: 'e' in certain contexts can be 'E' viseme
          if (letter === 'e' && !isFunctionWord && isFirstSyllable) {
            // Check for words that commonly use 'E' viseme
            const eWords = ['set', 'get', 'let', 'met', 'bet', 'wet', 'yet'];
            if (eWords.includes(lowerWord)) {
              phoneme = 'E'; // Use 'E' viseme for these words
            }
          }
        }

      if (phoneme) {
        phonemes.push({
          text: letter,
          phoneme: phoneme,
          start: i,
          end: i + 1
        });
      }
      i++;
    }
  }

  return phonemes;
}

/**
 * Detects letter combinations in text and converts them to phonemes
 * @param {string} text - The text to process
 * @returns {Array} Array of phoneme objects with position info
 */
function detectLetterCombinations(text) {
  const words = text.split(/\s+/);
  const allPhonemes = [];
  let globalIndex = 0;

  words.forEach(word => {
    const wordPhonemes = detectSchwaSounds(word);

    // Adjust global positions
    wordPhonemes.forEach(phoneme => {
      phoneme.start += globalIndex;
      phoneme.end += globalIndex;
    });

    allPhonemes.push(...wordPhonemes);
    globalIndex += word.length + 1; // +1 for space
  });

  return allPhonemes;
}

/**
 * Converts a letter to its approximate phoneme
 * @param {string} letter - The letter to convert
 * @returns {string} The phoneme symbol
 */
function letterToPhoneme(letter) {
  return LETTER_TO_PHONEME_MAP[letter] || LETTER_TO_PHONEME_MAP['default'];
}

/**
 * Converts a phoneme to its corresponding viseme
 * @param {string} phoneme - The phoneme to convert
 * @returns {string} The viseme symbol
 */
function phonemeToViseme(phoneme) {
  return PHONEME_TO_VISEME_MAP[phoneme] || PHONEME_TO_VISEME_MAP['default'];
}

/**
 * Determines if a phoneme is a vowel
 * @param {string} phoneme - The phoneme to check
 * @returns {boolean} Whether the phoneme is a vowel
 */
function isVowelPhoneme(phoneme) {
  const vowels = ['AA', 'AE', 'AH', 'AO', 'AW', 'AY', 'EH', 'EY', 'IH', 'IY', 'OW', 'OY', 'UH', 'UW'];
  return vowels.includes(phoneme);
}

/**
 * Determines if a letter is a vowel
 * @param {string} letter - The letter to check
 * @returns {boolean} Whether the letter is a vowel
 */
function isVowel(letter) {
  const vowels = ['a', 'e', 'i', 'o', 'u', 'A', 'E', 'I', 'O', 'U'];
  return vowels.includes(letter);
}

/**
 * Determines the duration for a phoneme based on its characteristics
 * @param {string} phoneme - The phoneme
 * @param {boolean} isStressed - Whether the phoneme is stressed
 * @param {boolean} isEndOfWord - Whether this is the end of a word
 * @param {boolean} isEndOfPhrase - Whether this is the end of a phrase
 * @returns {number} Duration in milliseconds
 */
function getPhonemeDuration(phoneme, isStressed = false, isEndOfWord = false, isEndOfPhrase = false) {
  const isVowel = isVowelPhoneme(phoneme);

  let baseDuration;

  if (isVowel) {
    if (isStressed) {
      baseDuration = PHONEME_DURATION.VOWEL_LONG;
    } else {
      baseDuration = PHONEME_DURATION.VOWEL_SHORT;
    }
  } else {
    // Consonants at end of words/phrases get longer duration
    if (isEndOfPhrase) {
      baseDuration = PHONEME_DURATION.CONSONANT_LONG;
    } else if (isEndOfWord) {
      baseDuration = PHONEME_DURATION.CONSONANT_MEDIUM;
    } else {
      baseDuration = PHONEME_DURATION.CONSONANT_SHORT;
    }
  }

  // Add natural variation (±15%) based on phoneme characteristics
  const seed = phoneme.charCodeAt(0) + (isStressed ? 1000 : 0) + (isEndOfWord ? 2000 : 0) + (isEndOfPhrase ? 4000 : 0);
  const variation = ((seed * 9301 + 49297) % 233280) / 233280 * 0.3 - 0.15; // ±15% variation
  const finalDuration = Math.max(50, baseDuration * (1 + variation));

  return Math.round(finalDuration);
}

/**
 * Analyzes text to determine stress patterns using syllable counting
 * @param {string} text - The text to analyze
 * @returns {Array} Array of boolean values indicating stress
 */
function analyzeStressPattern(text) {
  const words = text.toLowerCase().split(/\s+/);
  const stressPattern = [];

  words.forEach(word => {
    // Simple syllable counting and stress assignment
    const syllables = countSyllables(word);
    const letters = word.split('');

    letters.forEach((letter, index) => {
      const isVowelLetter = isVowel(letter);
      const isFirstVowel = isVowelLetter && !letters.slice(0, index).some(isVowel);

      // Stress the first syllable of multi-syllable words
      const isStressed = isVowelLetter && isFirstVowel && syllables > 1;
      stressPattern.push(isStressed);
    });

    // Add space between words
    if (words.indexOf(word) < words.length - 1) {
      stressPattern.push(false); // Space character
    }
  });

  return stressPattern;
}

/**
 * Counts syllables in a word using vowel counting
 * @param {string} word - The word to count syllables in
 * @returns {number} Number of syllables
 */
function countSyllables(word) {
  const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];
  let count = 0;
  let prevWasVowel = false;

  for (let i = 0; i < word.length; i++) {
    const isVowel = vowels.includes(word[i].toLowerCase());
    if (isVowel && !prevWasVowel) {
      count++;
    }
    prevWasVowel = isVowel;
  }

  // Ensure at least one syllable
  return Math.max(count, 1);
}

/**
 * Converts text to visemes with timing using improved phoneme-based approach
 * @param {string} text - The text to convert
 * @param {Object} options - Configuration options
 * @returns {Array} Array of viseme objects with timing
 */
function textToVisemes(text, options = {}) {
  const {
    baseTime = 0,
    stressPattern = null,
    wordSpacing = PHONEME_DURATION.WORD_SPACING,
    enableLogging = false
  } = options;

  // Process pause tags first
  const { text: textWithPauses, pausePositions } = processPauseTags(text);

  // Clean the text (remove punctuation, normalize)
  const cleanText = textWithPauses.replace(/[^\w\s]/g, '').trim();
  if (!cleanText) {
    return [{
      type: 'viseme',
      value: 'sil',
      time: baseTime
    }];
  }

  // Split into words
  const words = cleanText.split(/\s+/);
  const visemes = [];
  let currentTime = baseTime;

  words.forEach((word, wordIndex) => {
    // Check if this is a number (single digit)
    if (/^\d$/.test(word)) {
      // Use Polly's exact viseme sequence for numbers
      const numberVisemes = getPollyNumberVisemes(word);

      // Add each viseme from the number sequence
      numberVisemes.forEach((viseme, visemeIndex) => {
        visemes.push({
          type: 'viseme',
          value: viseme.value,
          time: currentTime
        });

        currentTime += viseme.duration;
      });

      return; // Skip normal phoneme processing for numbers
    }

    // Use improved letter combination detection for non-numbers
    const phonemes = detectLetterCombinations(word);

    phonemes.forEach((phoneme, phonemeIndex) => {
      const isEndOfWord = phonemeIndex === phonemes.length - 1;
      const isEndOfPhrase = wordIndex === words.length - 1 && isEndOfWord;
      const isStressed = stressPattern ? stressPattern[phoneme.start] : false;

      // Convert phoneme to viseme
      const viseme = phonemeToViseme(phoneme.phoneme);
      const duration = getPhonemeDuration(phoneme.phoneme, isStressed, isEndOfWord, isEndOfPhrase);

      visemes.push({
        type: 'viseme',
        value: viseme,
        time: currentTime
      });

      currentTime += duration;
    });

    // Add spacing between words (except for last word)
    if (wordIndex < words.length - 1) {
      currentTime += wordSpacing;
    }
  });

  // Insert pause visemes at their original positions
  if (pausePositions.length > 0) {
    pausePositions.forEach(pause => {
      // Calculate where to insert the pause based on text position
      const textPosition = pause.start;
      const totalTextLength = text.length;
      const visemeIndex = Math.floor((textPosition / totalTextLength) * visemes.length);

      // Insert silent viseme
      const insertTime = visemeIndex < visemes.length ? visemes[visemeIndex].time : currentTime;
      visemes.splice(visemeIndex, 0, {
        type: 'viseme',
        value: 'sil',
        time: insertTime
      });

      // Adjust timing for subsequent visemes
      for (let i = visemeIndex + 1; i < visemes.length; i++) {
        visemes[i].time += pause.duration;
      }
    });
  }

  // Add a silent viseme at the end
  visemes.push({
    type: 'viseme',
    value: 'sil',
    time: currentTime
  });

  return visemes;
}

/**
 * Generates Polly-style speech marks for arbitrary text
 * @param {string} text - The text to generate visemes for
 * @param {Object} options - Configuration options
 * @returns {Object} Polly-style speech marks object
 */
function generateVisemesFromText(text, options = {}) {
  const {
    baseTime = 0,
    wordSpacing = PHONEME_DURATION.WORD_SPACING,
    autoStress = true,
    enableLogging = false
  } = options;

  // Analyze stress pattern if auto-stress is enabled
  const stressPattern = autoStress ? analyzeStressPattern(text) : null;

  const visemes = textToVisemes(text, {
    baseTime,
    stressPattern,
    wordSpacing,
    enableLogging
  });

  return [
    {
      words: [
        {
          type: 'word',
          value: text,
          time: baseTime,
          visemes: visemes
        }
      ]
    }
  ];
}

/**
 * Generates visemes for multiple text segments
 * @param {Array} textSegments - Array of text strings
 * @param {Object} options - Configuration options
 * @returns {Object} Polly-style speech marks object
 */
function generateMultiTextVisemes(textSegments, options = {}) {
  const {
    baseTime = 0,
    wordSpacing = PHONEME_DURATION.WORD_SPACING,
    segmentSpacing = PHONEME_DURATION.PHRASE_SPACING
  } = options;

  let currentTime = baseTime;
  const sentences = [];

  textSegments.forEach((text, index) => {
    const visemes = textToVisemes(text, {
      baseTime: currentTime,
      wordSpacing
    });

    sentences.push({
      words: [
        {
          type: 'word',
          value: text,
          time: currentTime,
          visemes: visemes
        }
      ]
    });

    // Add spacing between segments
    if (index < textSegments.length - 1) {
      currentTime += segmentSpacing;
    }
  });

  return sentences;
}

/**
 * Creates a simple viseme sequence for testing
 * @param {string} text - The text
 * @param {number} duration - Total duration in milliseconds
 * @returns {Object} Simple viseme sequence
 */
function createSimpleVisemes(text, duration = 1000) {
  const visemeCount = Math.floor(duration / 120); // 120ms per viseme
  const visemes = ['a', 'e', 'i', 'o', 'u', 's', 't', 'r'];
  const visemeArray = [];

  for (let i = 0; i < visemeCount; i++) {
    visemeArray.push({
      type: 'viseme',
      value: visemes[i % visemes.length],
      time: (i * duration) / visemeCount
    });
  }

  // Add silent viseme at the end
  visemeArray.push({
    type: 'viseme',
    value: 'sil',
    time: duration
  });

  return [
    {
      words: [
        {
          type: 'word',
          value: text,
          time: 0,
          visemes: visemeArray
        }
      ]
    }
  ];
}

/**
 * Validates if a viseme is supported by the animation system
 * @param {string} viseme - The viseme to check
 * @returns {boolean} Whether the viseme is supported
 */
function isSupportedViseme(viseme) {
  const supportedVisemes = [
    'a', 'e', 'i', 'o', 'u', 's', 't', 'r', 'f', 'p', 'v', 'k',
    'sil', 'sil_teeth', '@', 'glance', 'exasperated', 'excited'
  ];
  return supportedVisemes.includes(viseme);
}

/**
 * Gets all supported visemes
 * @returns {Array} Array of supported viseme symbols
 */
function getSupportedVisemes() {
  return [
    'a', 'e', 'i', 'o', 'u', 's', 't', 'r', 'f', 'p', 'v', 'k',
    'sil', 'sil_teeth', '@', 'glance', 'exasperated', 'excited'
  ];
}

/**
 * Maps a letter to its closest supported viseme
 * @param {string} letter - The letter
 * @returns {string} The closest supported viseme
 */
function mapLetterToSupportedViseme(letter) {
  const viseme = phonemeToViseme(letterToPhoneme(letter));
  return isSupportedViseme(viseme) ? viseme : 'a';
}

/**
 * Generates visemes for a word (simplified interface)
 * @param {string} word - The word to generate visemes for
 * @param {Object} options - Configuration options
 * @returns {Object} Polly-style speech marks object
 */
function generateWordVisemes(word, options = {}) {
  return generateVisemesFromText(word, options);
}

/**
 * Generates visemes for multiple words
 * @param {Array} words - Array of word strings
 * @param {Object} options - Configuration options
 * @returns {Object} Polly-style speech marks object
 */
function generateMultiWordVisemes(words, options = {}) {
  return generateMultiTextVisemes(words, options);
}

/**
 * Generates visemes with fallback (always works with any text)
 * @param {string} text - The text to generate visemes for
 * @param {Object} options - Configuration options
 * @returns {Object} Polly-style speech marks object
 */
function generateVisemesWithFallback(text, options = {}) {
  return generateVisemesFromText(text, options);
}

/**
 * Analyzes text and returns letter-to-viseme mapping
 * @param {string} text - The text to analyze
 * @returns {Array} Array of {letter, viseme} objects
 */
function analyzeTextVisemes(text) {
  const letters = text.replace(/[^\w]/g, '').split('');
  return letters.map(letter => ({
    letter,
    viseme: phonemeToViseme(letterToPhoneme(letter))
  }));
}

/**
 * Generates visemes from text and audio buffer length
 * @param {string} inText - The input text (may contain tags)
 * @param {number} audioBufferLength - Length of audio buffer in bytes
 * @returns {Object} Polly-style speech marks object
 */
function generateVisemesFromTextAndAudio(inText, audioBufferLength, audioBuffer = null) {
  // Process pause tags first
  const { text: textWithPauses, pausePositions } = processPauseTags(inText);

  // Strip remaining tags from input text
  const cleanText = stripTags(textWithPauses);

  if (!cleanText || !cleanText.trim()) {
    return createSimpleVisemes('', 1000);
  }

  // Try to parse MP3 header to get actual sample rate
  let sampleRate = null;
  if (audioBuffer && audioBuffer instanceof Uint8Array) {
    sampleRate = parseMP3SampleRate(audioBuffer);
  }

  // Estimate duration from audio buffer length and sample rate
  const estimatedDuration = estimateAudioDuration(audioBufferLength, sampleRate);

  // Generate visemes based on text content
  const visemes = generateVisemesFromText(cleanText);
  const visemeArray = visemes[0].words[0].visemes;

  // Insert silent visemes at pause positions
  if (pausePositions.length > 0) {
    pausePositions.forEach(pause => {
      // Find the approximate position in the viseme array
      const textPosition = pause.start;
      const visemeIndex = Math.floor((textPosition / cleanText.length) * visemeArray.length);

      // Insert silent viseme with parsed duration
      visemeArray.splice(visemeIndex, 0, {
        type: 'viseme',
        value: 'sil',
        time: visemeArray[visemeIndex] ? visemeArray[visemeIndex].time : 0
      });

      // Adjust timing for subsequent visemes using the parsed duration
      for (let i = visemeIndex + 1; i < visemeArray.length; i++) {
        visemeArray[i].time += pause.duration;
      }
    });
  }

  // Scale timing to match estimated duration
  if (visemeArray.length > 1) {
    const originalDuration = visemeArray[visemeArray.length - 1].time;
    const scaleFactor = estimatedDuration / originalDuration;
    
    // Validate scale factor to prevent extreme compression or expansion
    const minScaleFactor = 0.3;  // Don't compress more than 70% (was 50%)
    const maxScaleFactor = 3.0;   // Don't expand more than 300%
    
    let finalScaleFactor = scaleFactor;
    if (scaleFactor < minScaleFactor) {
      finalScaleFactor = minScaleFactor;
    } else if (scaleFactor > maxScaleFactor) {
      finalScaleFactor = maxScaleFactor;
    }
    
    // Apply scaling with validation
    visemeArray.forEach(viseme => {
      viseme.time = Math.round(viseme.time * finalScaleFactor);
    });
  }

  // Add strategic mouth closures for more natural "pep"
  const enhancedVisemeArray = addMouthClosures(visemeArray);
  visemes[0].words[0].visemes = enhancedVisemeArray;

  // Fallback: If the estimated duration seems unreasonable, use text-based timing
  const finalDuration = enhancedVisemeArray[enhancedVisemeArray.length - 1].time;
  const textBasedDuration = cleanText.length * 150; // Rough estimate: 150ms per character
  
  // Check if audio-based duration is wildly different from text-based estimate
  const ratio = finalDuration / textBasedDuration;
  if (ratio > 4.0 || ratio < 0.25) { // If they differ by more than 4x in either direction
    // Audio estimation is probably wrong, fall back to text-based timing
    const fallbackVisemes = generateVisemesFromText(cleanText, {
      baseTime: 0,
      wordSpacing: 80
    });
    
    // Add padding to fallback visemes
    return addVisemePadding(fallbackVisemes);
  }


  
  // Add padding to the main visemes
  return addVisemePadding(visemes);
}

/**
 * Parses MP3 header to extract sample rate
 * @param {Uint8Array} audioBuffer - The audio buffer
 * @returns {number|null} Sample rate in Hz, or null if not found
 */
function parseMP3SampleRate(audioBuffer) {
  if (!audioBuffer || audioBuffer.length < 10) {
    return null;
  }

  // Look for MP3 sync word (0xFF 0xFB or 0xFF 0xFA)
  for (let i = 0; i < audioBuffer.length - 10; i++) {
    if (audioBuffer[i] === 0xFF && (audioBuffer[i + 1] & 0xE0) === 0xE0) {
      // Found MP3 sync word, parse header
      const header = audioBuffer[i + 1];
      const version = (header >> 3) & 0x03;
      const layer = (header >> 1) & 0x03;
      const bitrateIndex = (audioBuffer[i + 2] >> 4) & 0x0F;
      const sampleRateIndex = (audioBuffer[i + 2] >> 2) & 0x03;

      // Sample rate table for MPEG-1 Layer 3
      const sampleRates = [44100, 48000, 32000];

      if (version === 0x03 && layer === 0x01) { // MPEG-1 Layer 3
        const sampleRate = sampleRates[sampleRateIndex] || null;
        return sampleRate;
      }
    }
  }

  return null;
}

/**
 * Estimates audio duration from buffer length and sample rate
 * @param {number} audioBufferLength - Length of audio buffer in bytes
 * @param {number} sampleRate - Sample rate in Hz
 * @returns {number} Duration in milliseconds
 */
function estimateAudioDuration(audioBufferLength, sampleRate) {
  // For MP3 files, we need to use bitrate instead of sample rate
  // Try multiple common bitrates to get a better estimate
  const commonBitrates = [64000, 96000, 128000, 160000, 192000, 256000]; // 64kbps to 256kbps
  let bestDuration = 0;
  let bestBitrate = 0;
  
  for (const bitrate of commonBitrates) {
    const bytesPerSecond = bitrate / 8;
    const duration = (audioBufferLength / bytesPerSecond) * 1000;
    
    // Prefer longer durations (lower bitrates) as they're more likely to be accurate
    // and prevent visemes from ending before audio
    if (duration > bestDuration && duration < 30000) { // Max 30 seconds
      bestDuration = duration;
      bestBitrate = bitrate;
    }
  }
  
  // If no reasonable duration found, use a conservative estimate
  if (bestDuration === 0) {
    // Conservative estimate: assume 64kbps for safety (longer duration)
    const conservativeBitrate = 64000;
    const bytesPerSecond = conservativeBitrate / 8;
    bestDuration = (audioBufferLength / bytesPerSecond) * 1000;
    bestBitrate = conservativeBitrate;
  }
  
  // Add a safety buffer of 50% to prevent visemes from ending before audio
  const finalDuration = Math.max(bestDuration * 1.5, 1500); // Minimum 1.5 seconds
  
  // Additional validation: cap duration at reasonable limits
  const maxReasonableDuration = 15000; // 15 seconds max for most speech
  const validatedDuration = Math.min(finalDuration, maxReasonableDuration);
  
  return Math.round(validatedDuration);
}

/**
 * Adds strategic mouth closures for more natural "pep" in speech
 * @param {Array} visemes - Array of viseme objects
 * @returns {Array} Array of visemes with added closures
 */
function addMouthClosures(visemes) {
  if (!visemes || visemes.length === 0) {
    return visemes;
  }

  const enhancedVisemes = [];
  const openMouthVisemes = ['a', 'e', 'i', 'o', 'u', 'A', 'E', 'O'];
  
  for (let i = 0; i < visemes.length; i++) {
    const currentViseme = visemes[i];
    const isOpenMouth = openMouthVisemes.includes(currentViseme.value);
    
    // Add current viseme
    enhancedVisemes.push(currentViseme);
    
    // Add brief mouth closure after certain patterns
    if (i < visemes.length - 1) {
      const nextViseme = visemes[i + 1];
      const nextIsOpenMouth = openMouthVisemes.includes(nextViseme.value);
      
      // Add closure between consecutive open mouth visemes (but not too frequently)
      if (isOpenMouth && nextIsOpenMouth && i % 3 === 0) {
        enhancedVisemes.push({
          type: 'viseme',
          value: 'sil',
          time: currentViseme.time + 20 // Brief 20ms closure
        });
      }
      
      // Add closure before certain consonants for more natural speech
      const consonantVisemes = ['t', 'p', 'k', 's', 'f'];
      if (consonantVisemes.includes(nextViseme.value) && isOpenMouth) {
        enhancedVisemes.push({
          type: 'viseme',
          value: 'sil',
          time: currentViseme.time + 15 // Brief 15ms closure
        });
      }
    }
  }
  
  return enhancedVisemes;
}

/**
 * Adds 60 seconds of padding visemes (300ms closed mouth + random visemes) to speech marks
 * @param {Array} speechMarks - Polly-style speech marks array
 * @returns {Array} Speech marks with padding added
 */
function addVisemePadding(speechMarks) {
  const visemeArray = speechMarks[0].words[0].visemes;
  const lastVisemeTime = visemeArray[visemeArray.length - 1].time;
  const paddingStartTime = lastVisemeTime + 300; // 300ms of closed mouth
  
  // Add 300ms silent viseme
  visemeArray.push({
    type: 'viseme',
    value: 'sil',
    time: lastVisemeTime
  });
  
  const randomVisemes = generateRandomVisemes(paddingStartTime, 45000);
  visemeArray.push(...randomVisemes);
  
  return speechMarks;
}

// ES module exports
export {
  generateVisemesFromText,
  generateMultiTextVisemes,
  createSimpleVisemes,
  isSupportedViseme,
  getSupportedVisemes,
  mapLetterToSupportedViseme,
  generateWordVisemes,
  generateMultiWordVisemes,
  generateVisemesWithFallback,
  analyzeTextVisemes,
  generateVisemesFromTextAndAudio,
  addMouthClosures,
  addVisemePadding,
  generateRandomVisemes,
  createRandomSpeechMarks
};